// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { SettingModel, ISettingValues, ISettingModelState } from 'models/setting-model'
import { DeviceUpdateModel, IDeviceState, IDeviceUpdateModelState } from 'models/device-update-model'
import { IGeneralSetting } from 'api/setting'
import { IDevice } from 'constants/device'
import Emitter from 'mitt'

export interface IComposedDeviceSettingModelState {
  device: IDeviceState
  setting: ISettingValues
  deviceDirty: boolean
  settingDirty: boolean
}

export class ComposedDeviceSettingModel {
  private settingModel: SettingModel
  private deviceUpdateModel: DeviceUpdateModel

  public readonly emitter = Emitter()

  constructor (device: IDevice, setting: IGeneralSetting) {
    this.settingModel = new SettingModel(setting)
    this.deviceUpdateModel = new DeviceUpdateModel(device)
    this.settingModel.emitter.on('change', this.handleSettingModelChange)
    this.deviceUpdateModel.emitter.on('change', this.handleDeviceUpdateModelChange)
  }

  private handleDeviceUpdateModelChange = (state: IDeviceUpdateModelState) => {
    this.emitter.emit('change', this.getState())
  }

  private handleSettingModelChange = (state: ISettingModelState) => {
    this.emitter.emit('change', this.getState())
  }

  getState (): IComposedDeviceSettingModelState {
    const deviceState = this.deviceUpdateModel.getState()
    const settingState = this.settingModel.getState()
    return {
      device: deviceState.device,
      setting: settingState.setting,
      deviceDirty: deviceState.dirty,
      settingDirty: settingState.dirty
    }
  }

  setDevice (key: string, value: unknown) {
    this.deviceUpdateModel.set(key, value)
  }

  setSetting (key: string, value: unknown) {
    this.settingModel.set(key, value)
  }

  reset () {
    this.settingModel.reset()
    this.deviceUpdateModel.reset()
  }

  getDeviceModelDirtyFields () {
    return this.deviceUpdateModel.getDirtyFields()
  }

  getSettingModelDirtyFields () {
    return this.settingModel.buildUpdateParam(this.settingModel.getDirtySettings())
  }

  isDeviceModelDirty () {
    return this.deviceUpdateModel.isDirty()
  }

  isSettingModelDirty () {
    return this.settingModel.isDirty()
  }

  dispose () {
    this.settingModel.dispose()
    this.deviceUpdateModel.dispose()
  }
}
